/**
 * Lightweight conversion + engagement event tracking.
 * Fires to:
 *   1. Google Analytics 4 (via gtag) — for the site owner's GA dashboard
 *   2. window.dataLayer — for any future GTM integration
 *   3. Our own backend `/api/analytics/event` — persisted in Mongo so we can
 *      inspect the funnel without needing GA access
 */
import { getSessionId } from './session';
import { getFirstTouch } from './attribution';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GA_ID = process.env.REACT_APP_GA_ID;

const gtagSafe = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  } catch { /* SSR / privacy blocker safety */ }
};

export const trackEvent = (name, params = {}) => {
  if (!name) return;
  const sessionId = getSessionId();
  // Auto-attach the browser referrer + a couple of UTM params if present so
  // the founder analytics strip can group traffic sources without every call
  // site needing to remember to pass them.
  const enrichedParams = { ...params };
  try {
    if (!enrichedParams.referrer && document.referrer) enrichedParams.referrer = document.referrer;
    const qs = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((k) => {
      const v = qs.get(k);
      if (v && !enrichedParams[k]) enrichedParams[k] = v;
    });
    // First-touch attribution — record which channel FIRST brought this user.
    // Stays with them for every conversion event so we can see revenue-by-source.
    const ft = getFirstTouch();
    if (ft && ft.captured_at) {
      enrichedParams.first_touch_referrer = ft.referrer || '';
      enrichedParams.first_touch_utm_source = ft.utm_source || '';
      enrichedParams.first_touch_landing = ft.landing_path || '';
      enrichedParams.first_touch_at = ft.captured_at || '';
    }
  } catch { /* SSR safety */ }
  const payload = { name, params: enrichedParams, session_id: sessionId, path: window.location.pathname, ts: Date.now() };
  // 1. GA4
  gtagSafe('event', name, { ...enrichedParams, session_id: sessionId });
  // 2. dataLayer (for GTM or future tools that read it)
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...enrichedParams });
  } catch { /* noop */ }
  // 3. Backend log — fire-and-forget, never blocks UI
  try {
    axios.post(`${BACKEND_URL}/api/analytics/event`, payload).catch(() => {});
  } catch { /* offline */ }
};

/**
 * Fire a GA4 page_view for the current URL. Called on every React Router
 * route change so SPA navigations show up as separate page views in GA.
 * We keep send_page_view=false on the initial gtag('config') so this is the
 * single source of truth — no double counting.
 */
export const trackPageView = (path) => {
  const url = path || window.location.pathname + window.location.search;
  gtagSafe('event', 'page_view', {
    page_path: url,
    page_location: window.location.href,
    page_title: document.title,
  });
  if (GA_ID) {
    gtagSafe('config', GA_ID, { page_path: url, send_page_view: false });
  }
};

// Convenience shortcuts for the two conversions we care about most.
export const trackSignupStart = (source = 'unknown') => trackEvent('signup_start', { source });
export const trackCheckoutStart = (package_id, source = 'unknown') => trackEvent('checkout_start', { package_id, source });
